/**
 * The band under the hero: what an example person's labs and answers put in their formula, the
 * pouch being made from it, and the formula signed again after new labs at three months and at
 * six, played by scrolling.
 *
 * It is a real table first. Rows are the markers we read and the answers we use, with Jordan's
 * result. Columns are the ingredients, each with a small rendered pill. A cell is filled when that
 * row puts that ingredient in the formula, and says so in words for a screen reader. Everything
 * else is drawn on one WebGL canvas laid over the sticky stage, with each pill placed on the cell it
 * belongs to by measuring the cell every frame, so the 3D follows whatever the table's layout does.
 *
 * Eight numbered steps tell the story. From 900px they scroll past on the left while the stage
 * sticks on the right, each step's block as tall as its share of the sequence so the step in view
 * is the one playing. Below 900px the stage carries the current step itself. A progress bar marks
 * the first labs, three months and six months. Below 1280px the stage is too narrow for the table
 * and the pouches side by side at a readable size, so it shows one at a time: the table while
 * results are read, the pouches while a formula is signed and made.
 *
 * Month 1, Month 3 and Month 6 are buttons above the stage, and the month is in the address as
 * #month-3. Where the stage cannot fit (200% zoom, very short windows) or with reduced motion, the
 * section is one still screen that shows the chosen month, starting at month 1. Without WebGL, flat marks in the product colours
 * stand in for the pills and the pouch photograph for the pouches.
 */
import * as stylex from "@stylexjs/stylex";
import type { RefObject } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { announceJump, instant, useJump } from "./instant.ts";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/product.stylex";
import { product } from "@lattice/tokens/product.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { focusRing } from "@lattice/ui/focus";
import type { Finding, Ingredient as Dosed, Reading, Result } from "./example.ts";
import { examplePouch, findings, ingredients, inPouch } from "./example.ts";
import type { Box, Cell, Fate, Layout } from "./render/journey.ts";
import type { ColumnSpec, MapScene } from "./render/mapScene.ts";
import type { PieceKind } from "./render/pieces.ts";
import { firstOf, formulaAt, laterOf, leftAt, barAt, MONTH_ENDS, phaseAt, rescanAt, resultAt, rowAt, sceneAt, STEP_BOUNDS, STEP_OF } from "./render/timeline.ts";
import type { Tone } from "./render/Tones.tsx";
import { Tones } from "./render/Tones.tsx";
import { whileVisible } from "./render/visible.ts";
import { keepEntry } from "./router.ts";

/** Wide enough for the steps to scroll beside the stage. */
const SPLIT = "@media (min-width: 900px)";

/* The stage beside the steps, too narrow for the table and the pouches together. */
const MIDDLE = "@media (min-width: 900px) and (max-width: 1279px)";

/** Wide enough for the table and the pouches side by side in the stage. */
const SIDE = "@media (min-width: 1280px)";
const SCENES_QUERY = "(max-width: 1279px)";

/*
  Beside the steps on a short screen, which needs tighter rows to keep the whole table on screen,
  and on a tall one. The two never both match: when two media rules match, the order StyleX emits
  them in decides which wins, and that order is not the source order.
*/
const SHORT = "@media (min-width: 900px) and (max-height: 820px)";
const TALL = "@media (min-width: 900px) and (min-height: 821px)";

/*
  Wide enough for seven columns that each hold a level name and a dose, with the stage showing the
  table alone. From 1280px the table shares the stage with the pouch and has about 430px at any
  window width, because the stage stops growing, so it is laid out as a list of results with the
  ingredients each adds beside it, and the column names become a key of colours. So is every width
  under 1160px.
*/
const MATRIX = "@media (min-width: 1160px) and (max-width: 1279px)";

/* The list's rows hold the result on the left and its chips on the right, where there is room. */
const LISTROW = "@media (min-width: 480px) and (max-width: 1159px), (min-width: 1280px)";

/* The same two heights, for the matrix only, so a rule written for one never meets the other. */
const MATRIX_SHORT = "@media (min-width: 1160px) and (max-width: 1279px) and (max-height: 820px)";
const MATRIX_TALL = "@media (min-width: 1160px) and (max-width: 1279px) and (min-height: 821px)";

/*
  Where the stage cannot hold its content: 200% zoom on a laptop, a phone in landscape, a short
  window. The section then shows its still screen rather than sliding content out of reach.
*/
const STILL_QUERY = "(prefers-reduced-motion: reduce), (max-width: 899px) and (max-height: 559px), (min-width: 900px) and (max-height: 695px)";
const MID = "@media (min-width: 480px) and (max-width: 899px)";
const REDUCED = "@media (prefers-reduced-motion: reduce)";
const GUTTER = "clamp(16px, 4vw, 56px)";

/** How far the sequence scrolls, in viewport heights, before the section lets go. */
const LENGTH = 560;
interface Ingredient {
  readonly name: string;
  readonly short: string;
  readonly kind: PieceKind;
  readonly tones: readonly [Tone, Tone];
  readonly fate: Fate;
}
const NB = "‑";
const INGREDIENTS: readonly Ingredient[] = [{
  name: "Whey protein isolate",
  short: "Protein",
  kind: "scoop",
  tones: ["scoop", "whey"],
  fate: "scoop"
}, {
  name: "Creatine monohydrate",
  short: "Creatine",
  kind: "oval",
  tones: ["creatine", "creatine"],
  fate: "powder"
}, {
  name: "Vitamin D3",
  short: "Vitamin D3",
  kind: "tablet",
  tones: ["vitaminD", "vitaminD"],
  fate: "powder"
}, {
  name: "Magnesium glycinate",
  short: "Magnesium",
  kind: "capsule",
  tones: ["magnesium", "capsule"],
  fate: "powder"
}, {
  name: "Methylfolate",
  short: "Methylfolate",
  kind: "tablet",
  tones: ["folate", "folate"],
  fate: "powder"
}, {
  name: "Vitamin B12",
  short: "Vitamin B12",
  kind: "tablet",
  tones: ["b12", "b12"],
  fate: "powder"
}, {
  name: `Omega${NB}3 softgel`,
  short: `Omega${NB}3`,
  kind: "softgel",
  tones: ["omega3", "omega3"],
  fate: "beside"
}];

/**
 * The three stages of the example, in the order they play, which is also the order a screen reader
 * reads them. Each covers some of the eight beats of the timeline: the first labs, the signing, the
 * mixing and the sealing, then each retest and the formula it makes.
 */
const STAGES: readonly {
  readonly month: string;
  readonly labs: string;
  readonly formula: string;
}[] = [{
  month: "Month 1",
  labs: "Vitamin D and magnesium read low. Homocysteine reads high.",
  formula: `Vitamin D3, magnesium, methylfolate and B12, with protein and creatine. A physician signs, then it is mixed and shipped. Omega${NB}3 is an add${NB}on.`
}, {
  month: "Month 3",
  labs: `Vitamin D, magnesium and homocysteine read in range. Omega${NB}3 still reads low.`,
  formula: `Three doses come down. Omega${NB}3 and protein go up. A physician signs again.`
}, {
  month: "Month 6",
  labs: `Everything reads in range except omega${NB}3.`,
  formula: "Magnesium, in range twice, comes out and B12 halves. A physician signs again."
}];

/** The stage each of the eight timeline steps belongs to. */
const STAGE_OF_STEP: readonly number[] = [0, 0, 0, 0, 1, 1, 2, 2];

/** How much of the scroll a stage owns: the shares of its steps. */
function stageShare(stage: number): number {
  return STAGE_OF_STEP.reduce((total, owner, step) => owner === stage ? total + ((STEP_BOUNDS[step + 1] ?? 1) - (STEP_BOUNDS[step] ?? 0)) : total, 0);
}

/** Toggled onto the announcement so pressing the same month twice still changes its text. */
const ZERO_WIDTH = "\u200b";
const MILESTONES = ["Month 1", "Month 3", "Month 6"] as const;
type Month = 0 | 1 | 2;
const MONTHS: readonly Month[] = [0, 1, 2];
const MONTH_NUMBERS = [1, 3, 6] as const;

/** The month a `#month-3` style hash names, or null for any other hash. */
function monthOfHash(hash: string): Month | null {
  const found = MONTH_NUMBERS.findIndex(n => hash === `#month-${n}`);
  return found === 0 || found === 1 || found === 2 ? found : null;
}
const LAB_MONTHS = [1, 3, 6] as const;

/*
  The table's label says which labs are on screen and, when they differ, which formula's doses sit
  beside them. The new formula is signed after the labs, while the pouch has the stage, so at
  months 3 and 6 the cells still show the formula before.
*/
function cornerText(labs: number, doses: number): string {
  const month = LAB_MONTHS[labs] ?? 6;
  return doses === labs ? `Example: Jordan, month ${String(month)}` : `Example: Jordan, month ${String(month)} labs. The doses are still the month ${String(LAB_MONTHS[doses] ?? 1)} formula until a physician signs the next one.`;
}
const LAB_ROWS = findings.filter(finding => finding.reading !== "answer");
const ANSWER_ROWS = findings.filter(finding => finding.reading === "answer");

/* Rows in scan order, which is the order they appear in the table. */
const ROWS: readonly Finding[] = [...LAB_ROWS, ...ANSWER_ROWS];
const CELLS: readonly Cell[] = ROWS.flatMap((finding, row) => INGREDIENTS.flatMap((ingredient, column) => finding.adds.includes(ingredient.name) ? [{
  row,
  column
}] : []));

/* The rows that have a chip in some formula. The scan band rests only on these. */
const CHIP_ROWS: ReadonlySet<number> = new Set(CELLS.map(cell => cell.row));

/*
  Protein is set by two rows, training and weight. Its dose prints in the first of them only, and
  the second carries a mark, so the column never reads as two 30 g doses.
*/
const PRIMARY: readonly boolean[] = CELLS.map((cell, index) => CELLS.findIndex(other => other.column === cell.column) === index);
type Edition = 0 | 1 | 2;
const EDITION_LIST: readonly Edition[] = [0, 1, 2];

/** The ingredient's dose in a formula, or null once a formula has taken it out. */
function doseIn(item: Dosed, edition: Edition): string | null {
  if (edition === 0) return item.dose;
  return (edition === 1 ? item.next[0] : item.next[1]).dose;
}
function basisIn(item: Dosed, edition: Edition): string {
  if (edition === 0) return item.basis;
  return (edition === 1 ? item.next[0] : item.next[1]).basis;
}
const POUCH_INTRO: Record<Edition, string> = {
  0: "Jordan's first pouch, signed from the first labs",
  1: "Jordan's two pouches, version 2 in front, signed from new labs at three months",
  2: "Jordan's three pouches, version 3 in front, signed from new labs at six months"
};
const BEFORE: Record<Edition, Edition | null> = {
  0: null,
  1: 0,
  2: 1
};

/** What the pouch in front says, line by line, with the dose each line had in the formula before. */
function pouchLabel(edition: Edition): string {
  const before = BEFORE[edition];
  const lines = ingredients.flatMap(item => {
    const dose = doseIn(item, edition);
    if (dose === null) return [];
    const was = before === null ? null : doseIn(item, before);
    return [was !== null && was !== dose ? `${item.name} ${dose}, was ${was}` : `${item.name} ${dose}`];
  });
  return `${POUCH_INTRO[edition]}. ${lines.join(". ")}.`;
}
const MONTH_LABELS = ["First labs", "Month 3 labs", "Month 6 labs"] as const;

/**
 * The lab markers that were off at the first labs, with their result at this edition. Shown beside
 * the pouch below 1280px, where the table is out of sight while a formula is made.
 */
function resultsLine(edition: Edition): string {
  const marks = LAB_ROWS.filter(finding => finding.reading !== "in range").map(finding => {
    const result = edition === 0 ? finding : finding.next[edition - 1];
    return `${finding.name} ${result?.value ?? finding.value}, ${(result?.reading ?? finding.reading).toLowerCase()}`;
  });
  return `${MONTH_LABELS[edition]}: ${marks.join(". ")}. ${changesLine(edition)}`.trim();
}
const CHANGE_WORDS = ["Removed", "Lower", "Higher", "Same"] as const;

/**
 * What the formula did to each dose, grouped by its word: "Lower: Vitamin D3, magnesium. Same:
 * creatine." The first formula adds everything, which its pouch already lists.
 */
function changesLine(edition: Edition): string {
  if (edition === 0) return "";
  const groups = CHANGE_WORDS.flatMap(word => {
    const names = INGREDIENTS.flatMap(ingredient => {
      const dosed = DOSED.get(ingredient.name);
      return dosed && trend(dosed, edition) === word ? [ingredient.short] : [];
    });
    return names.length === 0 ? [] : [`${word}: ${names.join(", ")}.`];
  });
  return `New doses. ${groups.join(" ")}`;
}
const NOTES: Record<Edition, string> = {
  0: "Months 1 to 3. One scoop daily.",
  1: "Months 4 to 6. One scoop daily.",
  2: "From month 7. One scoop daily."
};
const amount = (dose: string) => Number.parseFloat(dose.replaceAll(",", ""));

/** How a formula moves the dose from the one before it. Removal is its own word. */
function trend(item: Dosed, edition: Edition): "Adds" | "Lower" | "Higher" | "Same" | "Removed" {
  if (edition === 0) return "Adds";
  const before = doseIn(item, edition === 1 ? 0 : 1);
  const now = doseIn(item, edition);
  if (now === null) return "Removed";
  if (before === null) return "Adds";
  const change = amount(now) - amount(before);
  if (change < 0) return "Lower";
  return change > 0 ? "Higher" : "Same";
}
const EDITIONS = EDITION_LIST.map(edition => ({
  person: examplePouch.person,
  stamp: examplePouch.stamp,
  number: `0412 v${edition + 1}`,
  note: NOTES[edition],
  lines: inPouch.map(item => {
    const now = doseIn(item, edition);
    const before = edition === 0 ? null : doseIn(item, edition === 1 ? 0 : 1);
    return {
      name: item.name,
      dose: now ?? "Removed",
      basis: basisIn(item, edition),
      was: before === null || before === now ? null : before
    };
  })
}));
const DOSED = new Map(ingredients.map(item => [item.name, item]));

/* Each filled cell's place in `CELLS`, filled in when the section mounts. */
const CELL_INDEX = new WeakMap<Element, number>();

/* When each filled cell's pill leaves it. Powdered pills go one by one in table order. */
const LEAVES: readonly number[] = CELLS.map((cell, index) => {
  const fate = INGREDIENTS[cell.column]?.fate ?? "powder";
  const order = CELLS.slice(0, index).filter(c => INGREDIENTS[c.column]?.fate === fate).length;
  return leftAt(fate, order);
});
const COLUMNS: readonly ColumnSpec[] = INGREDIENTS.map(ingredient => {
  const line = inPouch.findIndex(item => item.name === ingredient.name);
  return {
    kind: ingredient.kind,
    tones: ingredient.tones,
    fate: ingredient.fate,
    line: line === -1 ? null : line
  };
});
const FLAG: Record<Reading, string> = {
  low: "Low",
  high: "High",
  "in range": "In range",
  answer: ""
};
const styles = {
  intro: {
    ks0D6T: "x18zekx4",
    kUOVxO: "xvueqy4",
    kg3NbH: "xb2g7nt",
    kLKAdn: "x1j9m8v2",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x626il7",
    $$css: true
  },
  heading: {
    kogj98: "x1ghz6dp",
    kGuDYH: "x154s7bo",
    k63SB2: "xqu410z",
    kQqvRs: "x1ntj4s9",
    kb6lSQ: "x1d3j7mx",
    kLWn49: "x1e5flnj",
    kN2L0X: "x1w2vvpw",
    $$css: true
  },
  skipExample: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kAzted: "x1envrbd",
    kSGwAc: "xqcrz7y",
    kGuDYH: "x1rf3zrh",
    kMwMTN: "xzb3lse",
    kMnn75: "xujl8zx",
    kcSHmL: "xda060z",
    k1TLXF: "x25gfxr xtsjvlx",
    $$css: true
  },
  lead: {
    kogj98: "x1ghz6dp",
    ks0D6T: "xc0i79g",
    kGuDYH: "x11mdwsw",
    kMwMTN: "x1jty25b",
    kN2L0X: "x1fzhlzt",
    $$css: true
  },
  track: {
    kVAEAm: "x1n2onr6",
    ks0D6T: "x18zekx4",
    kUOVxO: "xvueqy4",
    kg3NbH: "xnjsko4 xbazhjn",
    k1xSpc: "x1lliihq x145x0s9",
    kumcoG: "x14ns5yd x1xl0gbf",
    k1C7PZ: "x1cgrbel",
    $$css: true
  },
  steps: {
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kB88ic: "xe8uvvx",
    kLKAdn: "xexx8yu xd3y05d",
    kGO01o: "x18d9i69 x1bffcsl",
    $$css: true
  },
  stepsHidden: {
    kVAEAm: "x10l6tqk xpqkicq",
    kzqmXN: "xjlxmcz x1n5zvcv",
    kZKoxP: "x1v7u6xw xpv658r",
    kVQacm: "xb3r6kr x126i3ax",
    kz4h6p: "x1hyvwdk xsmm3ln",
    $$css: true
  },
  stepsStill: {
    kLKAdn: "xjh1bw0",
    kGO01o: "x18d9i69",
    kg3NbH: "xb2g7nt xv4ixxy",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x15agyyk",
    $$css: true
  },
  step: {
    kB7OPa: "x9f619",
    k4rD7h: "xyta7yn",
    kGO01o: "x18d9i69 x1ezy49l",
    kMwMTN: "x1jty25b",
    k1ekBW: "xt3l3uh",
    kIyJzY: "x937rjr",
    $$css: true
  },
  stepNow: {
    kMwMTN: "xzb3lse",
    $$css: true
  },
  stepCard: {
    kVAEAm: "x7wzq59",
    k87sOh: "x185y1rg",
    k1xSpc: "xrvj5dj",
    kumcoG: "xpczwk3",
    k1C7PZ: "x1vdzsm",
    kfiyM8: "x10ukxgv",
    $$css: true
  },
  stepCardStill: {
    kVAEAm: "x1uhb9sk",
    $$css: true
  },
  number: {
    kbNqZ1: "xa1s216",
    kzqmXN: "x100vrsf",
    kZKoxP: "x1vqgdyp",
    k1xSpc: "xrvj5dj",
    kgQiWS: "x1ku5rj1",
    kaIpWk: "x1m3fsnl",
    kVAM5u: "x1j7zvbq",
    ksu8eU: "x1y0btm7",
    kMzoRj: "xdq7b4a",
    kcqcaj: "x1l1vl4p",
    k63SB2: "xqu410z",
    kMwMTN: "x1jty25b",
    k1ekBW: "xzo0q3i",
    kIyJzY: "x937rjr",
    $$css: true
  },
  numberDone: {
    kWkggS: "x1890um5",
    kVAM5u: "x1i01p7j",
    kMwMTN: "xzb3lse",
    $$css: true
  },
  numberNow: {
    kWkggS: "xmbr0q7",
    kVAM5u: "xtfhg7g",
    kMwMTN: "x1fb0272",
    $$css: true
  },
  stepTitle: {
    kogj98: "x1ghz6dp",
    kGuDYH: "x1u29x3t",
    k63SB2: "xqu410z",
    kb6lSQ: "x1d3j7mx",
    kLWn49: "x1e5flnj",
    kN2L0X: "x1w2vvpw",
    $$css: true
  },
  facts: {
    kogj98: "x1ghz6dp",
    keoZOQ: "x1ri67zl",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x626il7",
    $$css: true
  },
  stepBody: {
    kogj98: "x1ghz6dp",
    keoZOQ: "x152e2qk",
    ks0D6T: "x18t6ddr",
    kGuDYH: "x1rf3zrh",
    kMwMTN: "x1jty25b",
    kLWn49: "xbz0q3o",
    kN2L0X: "x1fzhlzt",
    $$css: true
  },
  visual: {
    kVAEAm: "x1n2onr6",
    kZKoxP: "x15fg0br xpv658r x3pvysb",
    $$css: true
  },
  visualStill: {
    kZKoxP: "xt7dq6l",
    $$css: true
  },
  stage: {
    kVAEAm: "x7wzq59 xr317jx",
    k87sOh: "x13vifvy",
    kZKoxP: "x187pvsn x3pvysb",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  stageStill: {
    kVAEAm: "x1n2onr6",
    kZKoxP: "xt7dq6l",
    $$css: true
  },
  inner: {
    kVAEAm: "x1n2onr6",
    kY2c9j: "x1ja2u2z",
    kZKoxP: "x5yr21d",
    kg3NbH: "xb2g7nt xv4ixxy",
    kLKAdn: "xeonxcd x170965d x1eeskg9",
    kGO01o: "xxh1dlw x1k1y2me xvydhls",
    kB7OPa: "x9f619",
    k1xSpc: "xrvj5dj",
    kumcoG: "x1mkdm3x xd6parj",
    kC13JO: "x1swg1mr x1oo4ovd xgr3due",
    k9llMU: "x1qxlq8j x19c25m2 x1gh87bp",
    kfiyM8: "x10ukxgv x18b05un",
    k1C7PZ: "x1ov5ho6",
    khm7nJ: "xqecrbx x1xje1ex",
    $$css: true
  },
  innerStill: {
    kZKoxP: "xt7dq6l",
    kGO01o: "xxh1dlw x1xhm6uz",
    $$css: true
  },
  head: {
    kJuA4N: "x4lrnbh",
    k1xSpc: "xrvj5dj x12iyxos",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kB88ic: "xe8uvvx",
    $$css: true
  },
  headStill: {
    k1xSpc: "x1s85apg",
    $$css: true
  },
  headStep: {
    kJuA4N: "x15r89dc",
    kSiTet: "xg01cxk",
    k3aq6I: "x1wx64ow xuupxpy",
    k1ekBW: "x6o7n8i",
    kIyJzY: "x149xavy",
    kAMwcw: "xa02eh3",
    $$css: true
  },
  headShown: {
    kSiTet: "x1hc1fzr",
    k3aq6I: "x1c071of",
    kIyJzY: "x3u5o1n",
    kIr0Dl: "xwt041n",
    $$css: true
  },
  headTitle: {
    kogj98: "x1ghz6dp",
    kGuDYH: "x1u29x3t",
    k63SB2: "xqu410z",
    kb6lSQ: "x1d3j7mx",
    kLWn49: "x1e5flnj",
    $$css: true
  },
  headBody: {
    kogj98: "x1ghz6dp",
    keoZOQ: "x152e2qk",
    kGuDYH: "x1rf3zrh",
    kMwMTN: "x1jty25b",
    kLWn49: "xbz0q3o",
    kN2L0X: "x1fzhlzt",
    $$css: true
  },
  progress: {
    kJuA4N: "xajvbnr",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1r0x45p",
    $$css: true
  },
  milestoneBox: {
    kVAEAm: "x1n2onr6",
    kzQI83: "x1iyjqo2",
    $$css: true
  },
  milestones: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x185a7wo",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kLKAdn: "xyinxu5",
    k1C7PZ: "xx02khn",
    kGuDYH: "xl4dl4e",
    kMwMTN: "x1jty25b",
    $$css: true
  },
  milestone: {
    k1xSpc: "x1lliihq",
    kzqmXN: "xh8yej3",
    kAzted: "x1envrbd",
    kogj98: "x1ghz6dp",
    k8WAf4: "x1jyo4x1",
    kg3NbH: "x1lq7ia9",
    kMzoRj: "xdq7b4a",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xtkjqb x19omjqh",
    kaIpWk: "x1fbwzx",
    kWkggS: "xjbqb8w",
    kVVagm: "xln7xf2",
    kMwMTN: "xzb3lse",
    k9WMMc: "x2b8uid",
    kkrTdU: "x1ypdohk",
    k1ekBW: "xt3l3uh",
    kIyJzY: "x937rjr",
    $$css: true
  },
  railDot: {
    kVAEAm: "x10l6tqk",
    k87sOh: "x13vifvy",
    kzqmXN: "x1fsd2vl",
    kZKoxP: "x170jfvy",
    keTefX: "x137kccz",
    kaIpWk: "x1m3fsnl",
    kWkggS: "x1krkpam",
    k1ekBW: "x15406qy",
    kIyJzY: "x937rjr",
    $$css: true
  },
  railDotStart: {
    kbCHJM: "x1rvfpmx",
    $$css: true
  },
  railDotMiddle: {
    kbCHJM: "x1nrll8i",
    $$css: true
  },
  railDotEnd: {
    kbCHJM: "x19m0gsf",
    $$css: true
  },
  railDotReached: {
    kWkggS: "xmbr0q7",
    $$css: true
  },
  milestoneNow: {
    kWkggS: "xmbr0q7",
    kVAM5u: "xtfhg7g",
    kMwMTN: "x1fb0272",
    k63SB2: "xqu410z",
    $$css: true
  },
  rail: {
    kVAEAm: "x10l6tqk",
    k87sOh: "xxk6nc9",
    kbCHJM: "xu96u03",
    kCIrl2: "x3m8u43",
    kZKoxP: "xnp4br5",
    kaIpWk: "x1m3fsnl",
    kWkggS: "x1krkpam",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  fill: {
    k1xSpc: "x1lliihq",
    kZKoxP: "x5yr21d",
    kzqmXN: "xh8yej3",
    kWkggS: "xmbr0q7",
    k3nNDw: "x4uogl2",
    k3aq6I: "x1ufz0iv",
    k1ekBW: "x11xpdln",
    kIyJzY: "x937rjr",
    $$css: true
  },
  tableBox: {
    kJuA4N: "x1bagk7n",
    kSGwAc: "x1y8v6su",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  table: {
    k1xSpc: "x1lliihq x2qd0eg",
    kzqmXN: "xh8yej3",
    kLvRdT: "x1mwwwfo",
    kV37R7: "xhdg8po",
    kPzzVL: "x140o2bo",
    kcqcaj: "x1l1vl4p",
    k3nNDw: "x1hj3fc7",
    k1ekBW: "x19991ni",
    kIyJzY: "x937rjr",
    $$css: true
  },
  part: {
    k1xSpc: "x1lliihq x1rrf47d",
    $$css: true
  },
  partHead: {
    k1xSpc: "x1lliihq x1pedx6l",
    $$css: true
  },
  headRow: {
    k1xSpc: "x78zum5 x1yomz4y",
    kwnvtZ: "x1a02dak",
    kGNEyG: "x6s0dn4",
    k1C7PZ: "x1vdzsm",
    khm7nJ: "xcfm6fe",
    kGO01o: "xk0dswh",
    $$css: true
  },
  rowHeadCol: {
    kzqmXN: "xrmyp2 xroyate xrnwunz xwqd1rt",
    $$css: true
  },
  corner: {
    k1xSpc: "x1lliihq x18eilp1",
    kCS8Yb: "x1l7klhg",
    k9WMMc: "x1yc453h",
    kXLuUW: "x3ajldb",
    kGuDYH: "x2q0hrr",
    k63SB2: "xqu410z",
    kMwMTN: "xzb3lse",
    kGO01o: "xk0dswh",
    kAzted: "x103ahzr xm46snq",
    $$css: true
  },
  legend: {
    k1xSpc: "x1lliihq x1fiv64o",
    k8WAf4: "x1jyo4x1",
    kGuDYH: "x2q0hrr x1n6zpv0",
    kMwMTN: "xzb3lse",
    k9WMMc: "x1yc453h",
    $$css: true
  },
  colHead: {
    k1xSpc: "x1s85apg x18eilp1",
    kXLuUW: "x3ajldb",
    kGO01o: "xk0dswh",
    kg3NbH: "xnjsko4 x1gw11ll",
    kGuDYH: "x2q0hrr x1n6zpv0",
    k63SB2: "xqu410z",
    kLWn49: "x1e5flnj",
    $$css: true
  },
  colHeadInner: {
    k1xSpc: "x78zum5",
    kXwgrk: "x1q0g3np x16yl27p",
    kGNEyG: "x6s0dn4",
    kOIVth: "xskm3c9 x9utrdf",
    $$css: true
  },
  colName: {
    k9WMMc: "x2b8uid",
    $$css: true
  },
  headSlot: {
    k1xSpc: "x1lliihq",
    kzqmXN: "x17z2i9w",
    kZKoxP: "x1qx5ct2",
    kmuXW: "x2lah0s",
    $$css: true
  },
  row: {
    k1xSpc: "x78zum5 xur4fe9 x1yomz4y",
    kumcoG: "xy6017b",
    kwnvtZ: "x1a02dak",
    kGNEyG: "x6s0dn4",
    k1C7PZ: "xx02khn",
    khm7nJ: "xcfm6fe x1t1ryef",
    k8WAf4: "x1jyo4x1 x9199uq xnrywdu",
    kL6WhQ: "x1t74ka8",
    kfdmCh: "x1q0q8m5",
    kt9PQ7: "xq3vdme",
    k1ekBW: "x15406qy",
    kIyJzY: "x937rjr",
    $$css: true
  },
  reading: {
    kWkggS: "x1890um5",
    $$css: true
  },
  rowHead: {
    k1xSpc: "x1lliihq x18eilp1",
    kCS8Yb: "x1l7klhg",
    kBCFzs: "xm5moh1 x18r1ulw",
    kbNqZ1: "x15daork xxj6drn",
    k9WMMc: "x1yc453h",
    k63SB2: "xox936i",
    k8WAf4: "xt970qd x66opgc xgic4zv",
    kZCmMZ: "x1c1uobl x1hm6eoy",
    kwRFfy: "xyri2b x1qxuy4o",
    kZKoxP: "xt7dq6l xnveyj5 x1mbtngh",
    kB7OPa: "x9f619",
    kLWn49: "x1e5flnj",
    $$css: true
  },
  marker: {
    k1xSpc: "x1lliihq",
    k63SB2: "xqu410z",
    kGuDYH: "x2q0hrr x1pa2vcq xfp7wzn",
    $$css: true
  },
  result: {
    k1xSpc: "x1lliihq",
    kGuDYH: "x2q0hrr x1n6zpv0",
    kMwMTN: "x1jty25b",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  flagged: {
    kMwMTN: "xzb3lse",
    k63SB2: "xqu410z",
    $$css: true
  },
  dot: {
    k1xSpc: "x1rg5ohu",
    kzqmXN: "x1v4s8kt",
    kZKoxP: "xols6we",
    k71WvV: "x15302su",
    kXLuUW: "x19zj4b6",
    kaIpWk: "x1m3fsnl",
    kWkggS: "x862vy8",
    $$css: true
  },
  inRange: {
    kMwMTN: "x1p2vmyc",
    $$css: true
  },
  cell: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x3nfvp2 x18eilp1",
    kBCFzs: "xm5moh1 x16x4vp2",
    kFhvOy: "x1lqcxt8",
    ks0D6T: "x193iq5w",
    kwnvtZ: "x1a02dak",
    kGNEyG: "x6s0dn4",
    kOIVth: "xskm3c9",
    kAzted: "x1envrbd x12dvgrk",
    kB7OPa: "x9f619",
    kmVPX3: "x1xqhfp xxwjgh1",
    kZCmMZ: "x29k06c x1qt9s2m",
    kwRFfy: "xxv5x7m x1tund5y",
    kaIpWk: "x1fbwzx xhyohiv",
    k9WMMc: "x2b8uid",
    kXLuUW: "xxymvpz",
    $$css: true
  },
  taken: {
    k3XXqK: "x107h2d7",
    kMeerF: "x8yc38l",
    kjBf7l: "x1mfawy8",
    kInvED: "x1jb3n4g",
    $$css: true
  },
  higher: {
    kWkggS: "x1ed06pq",
    $$css: true
  },
  lower: {
    kWkggS: "x1890um5",
    $$css: true
  },
  same: {
    kWkggS: "xjbqb8w",
    kGVxlE: "x5yh5ye",
    $$css: true
  },
  cornerFree: {
    kAzted: "x2lwn1j",
    $$css: true
  },
  noAdd: {
    k1xSpc: "x1s85apg x18eilp1",
    kmVPX3: "x1717udv",
    $$css: true
  },
  chipDot: {
    k1xSpc: "x1rg5ohu xevc75t",
    kzqmXN: "xeh5slm",
    kZKoxP: "x3ycs4o",
    kmuXW: "x2lah0s",
    kaIpWk: "x1m3fsnl",
    kGVxlE: "xqc1mce",
    $$css: true
  },
  chipDotHidden: {
    k1xSpc: "x1s85apg",
    $$css: true
  },
  listName: {
    k1xSpc: "xt0psk2 xevc75t",
    kGuDYH: "x2q0hrr",
    k63SB2: "xqu410z",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  filled: {
    kWkggS: "x3es8l1",
    k1ekBW: "x15406qy",
    kIyJzY: "x937rjr",
    $$css: true
  },
  empty: {
    kWkggS: "xjbqb8w",
    $$css: true
  },
  slot: {
    k1xSpc: "x1rg5ohu",
    kzqmXN: "x17z2i9w",
    kZKoxP: "x17rw0jw",
    kXLuUW: "xxymvpz",
    $$css: true
  },
  swatch: {
    kaIpWk: "x1m3fsnl",
    kGVxlE: "xqc1mce",
    $$css: true
  },
  mark: {
    kVAEAm: "x1uhb9sk x2elcj4",
    kpwlN0: "x1anvkwx xjoa13v",
    k1xSpc: "x78zum5",
    kXwgrk: "x1q0g3np x16yl27p",
    kOIVth: "xskm3c9 x10pah57",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kGuDYH: "x2q0hrr x1n6zpv0",
    k63SB2: "xqu410z",
    kMwMTN: "xzb3lse",
    khDVqt: "xuxw1ft",
    kSiTet: "x1hc1fzr x1tsdei3",
    k1ekBW: "x19991ni",
    kIyJzY: "x937rjr",
    $$css: true
  },
  markShown: {
    kSiTet: "x1hc1fzr",
    $$css: true
  },
  markGone: {
    k1xSpc: "x1s85apg",
    $$css: true
  },
  stack: {
    k1xSpc: "x78zum5",
    kXwgrk: "x1q0g3np x16yl27p",
    kOIVth: "xskm3c9 x10pah57",
    kGNEyG: "x6s0dn4",
    kLWn49: "x1uo3zyz",
    $$css: true
  },
  word: {
    k63SB2: "xox936i",
    $$css: true
  },
  amount: {
    k63SB2: "xqu410z",
    $$css: true
  },
  visualResult: {
    k1xSpc: "xt0psk2",
    $$css: true
  },
  visualGone: {
    k1xSpc: "x1s85apg",
    $$css: true
  },
  hidden: {
    kVAEAm: "x10l6tqk",
    kzqmXN: "xjlxmcz",
    kZKoxP: "x1v7u6xw",
    kVQacm: "xb3r6kr",
    kz4h6p: "x1hyvwdk",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  side: {
    kJuA4N: "x1sgvmt9 xulbtqr",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kGNEyG: "x6s0dn4",
    kOIVth: "x626il7",
    k7Eaqz: "xeuugli",
    kVAEAm: "x10l6tqk xatg0b3",
    kbCHJM: "xu96u03 x1nr4raw",
    kCIrl2: "x3m8u43 x1ho9cri",
    krVfgx: "xj9fbq8 xu4op86 xs37dp",
    kg3NbH: "xb2g7nt xv4ixxy",
    kB7OPa: "x9f619",
    $$css: true
  },
  results: {
    kogj98: "x1ghz6dp",
    kSGwAc: "xkh2ocl",
    kGuDYH: "x2q0hrr",
    kMwMTN: "xzb3lse",
    kN2L0X: "x1fzhlzt",
    k1xSpc: "x1lliihq xav5vni",
    $$css: true
  },
  resultsStill: {
    k1xSpc: "x1s85apg",
    $$css: true
  },
  sideStill: {
    kVAEAm: "x1uhb9sk",
    kJuA4N: "x52lvju xulbtqr",
    kg3NbH: "xnjsko4",
    $$css: true
  },
  pouch: {
    kVAEAm: "x1n2onr6",
    kzqmXN: "xh8yej3",
    kzQI83: "x1iyjqo2",
    kAzted: "x2lwn1j",
    $$css: true
  },
  pouchStill: {
    kzQI83: "x1c4vz4f",
    kZKoxP: "x1ki1rwx xlsnlc",
    $$css: true
  },
  flat: {
    kVAEAm: "x10l6tqk",
    kpwlN0: "x10a8y8t",
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    kVIFPx: "x19kjcj4",
    $$css: true
  },
  flatHidden: {
    k1xSpc: "x1s85apg",
    $$css: true
  },
  canvas: {
    kVAEAm: "x10l6tqk",
    kpwlN0: "x10a8y8t",
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    kfzvcC: "x47corl",
    kY2c9j: "x1vjfegm",
    $$css: true
  }
};
const SWATCH = {
  scoop: {
    kWkggS: "x1h3r81n",
    $$css: true
  },
  creatine: {
    kWkggS: "x68yugk",
    $$css: true
  },
  vitaminD: {
    kWkggS: "xnj9xg2",
    $$css: true
  },
  magnesium: {
    kWkggS: "x63iwpo",
    $$css: true
  },
  folate: {
    kWkggS: "x190g0we",
    $$css: true
  },
  b12: {
    kWkggS: "xysepj2",
    $$css: true
  },
  omega3: {
    kWkggS: "xv2l9pe",
    $$css: true
  },
  whey: {
    kWkggS: "x1p82lfq",
    $$css: true
  },
  capsule: {
    kWkggS: "xa4f5hx",
    $$css: true
  },
  film: {
    kWkggS: "xp45gcx",
    $$css: true
  },
  ink: {
    kWkggS: "x17bqms0",
    $$css: true
  },
  dim: {
    kWkggS: "x1hgeuy3",
    $$css: true
  },
  signal: {
    kWkggS: "xovxxy8",
    $$css: true
  }
};
const ROW_IDLE = {
  className: "x78zum5 xur4fe9 x1yomz4y xy6017b x1a02dak x6s0dn4 xx02khn xcfm6fe x1t1ryef x1jyo4x1 x9199uq xnrywdu x1t74ka8 x1q0q8m5 xq3vdme x15406qy x937rjr"
}.className ?? "";
const ROW_READING = {
  className: "x78zum5 xur4fe9 x1yomz4y xy6017b x1a02dak x6s0dn4 xx02khn xcfm6fe x1t1ryef x1jyo4x1 x9199uq xnrywdu x1t74ka8 x1q0q8m5 xq3vdme x15406qy x937rjr x1890um5"
}.className ?? "";
const CELL_NONE = {
  className: "x1n2onr6 xm5moh1 x16x4vp2 x1lqcxt8 x193iq5w x1a02dak x6s0dn4 xskm3c9 x1envrbd x12dvgrk x9f619 x29k06c x1qt9s2m xxv5x7m x1tund5y x1fbwzx xhyohiv x2b8uid xxymvpz x1s85apg x18eilp1 x1717udv"
}.className ?? "";
const CELL_WAITING = {
  className: "x1n2onr6 x3nfvp2 x18eilp1 xm5moh1 x16x4vp2 x1lqcxt8 x193iq5w x1a02dak x6s0dn4 xskm3c9 x1envrbd x12dvgrk x9f619 x1xqhfp xxwjgh1 x29k06c x1qt9s2m xxv5x7m x1tund5y x1fbwzx xhyohiv x2b8uid xxymvpz xjbqb8w"
}.className ?? "";
const CELL_FILLED = {
  className: "x1n2onr6 x3nfvp2 x18eilp1 xm5moh1 x16x4vp2 x1lqcxt8 x193iq5w x1a02dak x6s0dn4 xskm3c9 x1envrbd x12dvgrk x9f619 x1xqhfp xxwjgh1 x29k06c x1qt9s2m xxv5x7m x1tund5y x1fbwzx xhyohiv x2b8uid xxymvpz x3es8l1 x15406qy x937rjr"
}.className ?? "";
const CELL_TAKEN = {
  className: "x1n2onr6 x3nfvp2 x18eilp1 xm5moh1 x16x4vp2 x1lqcxt8 x193iq5w x1a02dak x6s0dn4 xskm3c9 x1envrbd x12dvgrk x9f619 x1xqhfp xxwjgh1 x29k06c x1qt9s2m xxv5x7m x1tund5y x1fbwzx xhyohiv x2b8uid xxymvpz x107h2d7 x8yc38l x1mfawy8 x1jb3n4g"
}.className ?? "";
const CELL_HIGHER = {
  className: "x1n2onr6 x3nfvp2 x18eilp1 xm5moh1 x16x4vp2 x1lqcxt8 x193iq5w x1a02dak x6s0dn4 xskm3c9 x1envrbd x12dvgrk x9f619 x1xqhfp xxwjgh1 x29k06c x1qt9s2m xxv5x7m x1tund5y x1fbwzx xhyohiv x2b8uid xxymvpz x15406qy x937rjr x1ed06pq"
}.className ?? "";
const CELL_LOWER = {
  className: "x1n2onr6 x3nfvp2 x18eilp1 xm5moh1 x16x4vp2 x1lqcxt8 x193iq5w x1a02dak x6s0dn4 xskm3c9 x1envrbd x12dvgrk x9f619 x1xqhfp xxwjgh1 x29k06c x1qt9s2m xxv5x7m x1tund5y x1fbwzx xhyohiv x2b8uid xxymvpz x15406qy x937rjr x1890um5"
}.className ?? "";
const CELL_SAME = {
  className: "x1n2onr6 x3nfvp2 x18eilp1 xm5moh1 x16x4vp2 x1lqcxt8 x193iq5w x1a02dak x6s0dn4 xskm3c9 x1envrbd x12dvgrk x9f619 x1xqhfp xxwjgh1 x29k06c x1qt9s2m xxv5x7m x1tund5y x1fbwzx xhyohiv x2b8uid xxymvpz x15406qy x937rjr xjbqb8w x5yh5ye"
}.className ?? "";
const CELL_OF_WORD = {
  Adds: CELL_FILLED,
  Higher: CELL_HIGHER,
  Lower: CELL_LOWER,
  Same: CELL_SAME,
  Removed: CELL_TAKEN
};

/* Each ingredient's chip dot, in its colour, drawn or not. */
const DOTS = new Map(INGREDIENTS.map(ingredient => {
  const tone = SWATCH[ingredient.tones[0]];
  return [ingredient.name, {
    shown: stylex.props(styles.chipDot, tone).className ?? "",
    hidden: stylex.props(styles.chipDot, styles.chipDotHidden, tone).className ?? ""
  }];
}));
const RESULT_SHOWN = {
  className: "xt0psk2"
}.className ?? "";
const RESULT_GONE = {
  className: "x1s85apg"
}.className ?? "";
const HEAD_STEP = {
  className: "x15r89dc xg01cxk x1wx64ow xuupxpy x6o7n8i x149xavy xa02eh3"
}.className ?? "";
const HEAD_STEP_SHOWN = {
  className: "x15r89dc x6o7n8i xa02eh3 x1hc1fzr x1c071of x3u5o1n xwt041n"
}.className ?? "";
const MILESTONE_CLASSES = MILESTONES.map(() => ({
  idle: stylex.props(styles.milestone, focusRing).className ?? "",
  now: stylex.props(styles.milestone, styles.milestoneNow, focusRing).className ?? ""
}));
const RAIL_DOTS = [styles.railDotStart, styles.railDotMiddle, styles.railDotEnd].map(at => ({
  idle: stylex.props(styles.railDot, at).className ?? "",
  reached: stylex.props(styles.railDot, at, styles.railDotReached).className ?? ""
}));
interface StepClasses {
  readonly step: string;
  readonly number: string;
}

/** A step block's classes for each of its states. */
function stepClasses() {
  const make = (now: boolean, number: typeof styles.numberNow | null): StepClasses => ({
    step: {
      0: {
        className: "x9f619 xyta7yn x18d9i69 x1ezy49l x1jty25b xt3l3uh x937rjr"
      },
      1: {
        className: "x9f619 xyta7yn x18d9i69 x1ezy49l xt3l3uh x937rjr xzb3lse"
      }
    }[!!now << 0].className ?? "",
    number: stylex.props(styles.number, number).className ?? ""
  });
  return {
    next: make(false, null),
    now: make(true, styles.numberNow),
    done: make(false, styles.numberDone)
  };
}
interface MarkClasses {
  readonly hidden: string;
  readonly shown: string;
  readonly gone: string;
}
const MARK_GONE = {
  className: "x1uhb9sk x2elcj4 x1anvkwx xjoa13v x1q0g3np x16yl27p xskm3c9 x10pah57 x6s0dn4 xl56j7k x2q0hrr x1n6zpv0 xqu410z xzb3lse xuxw1ft x1hc1fzr x1tsdei3 x19991ni x937rjr x1s85apg"
}.className ?? "";

/* What replaces the pill once it has left: a word and an amount. */
const WIDE_MARK: MarkClasses = {
  hidden: {
    className: "x1uhb9sk x2elcj4 x1anvkwx xjoa13v x78zum5 x1q0g3np x16yl27p xskm3c9 x10pah57 x6s0dn4 xl56j7k x2q0hrr x1n6zpv0 xqu410z xzb3lse xuxw1ft x1hc1fzr x1tsdei3 x19991ni x937rjr"
  }.className ?? "",
  shown: {
    className: "x1uhb9sk x2elcj4 x1anvkwx xjoa13v x78zum5 x1q0g3np x16yl27p xskm3c9 x10pah57 x6s0dn4 xl56j7k x2q0hrr x1n6zpv0 xqu410z xzb3lse xuxw1ft x19991ni x937rjr x1hc1fzr"
  }.className ?? "",
  gone: MARK_GONE
};

/**
 * `live` draws in 3D, `flat` has no usable WebGL and shows marks and the photograph while the
 * table still follows the scroll, and `still` is one screen with nothing moving.
 */
type Mode = "pending" | "live" | "flat" | "still";
const SCENE_MODE: Record<"pending" | "3d" | "none", Mode> = {
  pending: "pending",
  "3d": "live",
  none: "flat"
};
function ReadingOf({
  result
}: {
  readonly result: Result;
}) {
  const flagged = result.reading === "low" || result.reading === "high";
  return <>
      {result.value}
      {result.reading === "answer" ? null : <span {...{
      0: {
        className: "x1p2vmyc"
      },
      1: {
        className: "xzb3lse xqu410z"
      }
    }[!!flagged << 0]}>
          {" "}
          {flagged ? <span aria-hidden="true" className="x1rg5ohu x1v4s8kt xols6we x15302su x19zj4b6 x1m3fsnl x862vy8" /> : null}
          {FLAG[result.reading]}
        </span>}
    </>;
}
const resultsOf = (finding: Finding): readonly Result[] => [{
  value: finding.value,
  reading: finding.reading
}, ...finding.next];

/** What a screen reader hears for a row: all three results, whichever one is on screen. */
function said(finding: Finding): string {
  const [first, three, six] = resultsOf(finding).map(result => result.reading === "answer" ? result.value : `${result.value}, ${FLAG[result.reading]}`);
  return `${first ?? ""}. At three months, ${three ?? ""}. At six months, ${six ?? ""}.`;
}

/** The word and dose a formula gives one ingredient, as a sentence: "Lower, 1,000 IU, was 2,000 IU". */
function changeSaid(dosed: Dosed, edition: Edition): string {
  const word = trend(dosed, edition);
  const now = doseIn(dosed, edition);
  const before = edition === 0 ? null : doseIn(dosed, edition === 1 ? 0 : 1);
  if (word === "Removed") return `Removed, was ${before ?? ""}`;
  if (word === "Same" || before === null) return `${word}, ${now ?? ""}`;
  return `${word}, ${now ?? ""}, was ${before}`;
}

/**
 * What a screen reader hears for a filled cell: each formula's word and dose, since the three
 * words on screen are one at a time. The second protein cell names no dose, as on screen.
 */
function cellSaid(ingredient: Ingredient, primary: boolean): string {
  const dosed = DOSED.get(ingredient.name);
  if (!primary) return `Adds ${ingredient.name} with the row above`;
  if (!dosed) return `Adds ${ingredient.name}`;
  return `Adds ${ingredient.name}, ${dosed.dose}. At three months: ${changeSaid(dosed, 1)}. At six months: ${changeSaid(dosed, 2)}.`;
}

/** The word and the amount a filled cell shows for a formula: "Lower 1,000 IU", "Removed was 100 mg". */
function Mark({
  dosed,
  edition,
  partner
}: {
  readonly dosed: Dosed | undefined;
  readonly edition: Edition;
  readonly partner: string | null;
}) {
  if (!dosed) return <span className="x78zum5 x1q0g3np x16yl27p xskm3c9 x10pah57 x6s0dn4 x1uo3zyz">
        <span className="xox936i">Adds</span>
      </span>;

  /* Protein is set by two rows. The second carries no dose of its own, says the same word as the
     first, and names the row that sets it. */
  if (partner !== null) return <span className="x78zum5 x1q0g3np x16yl27p xskm3c9 x10pah57 x6s0dn4 x1uo3zyz">
        <span className="xox936i">{trend(dosed, edition)}</span>
        <span className="xqu410z">{doseIn(dosed, edition) ?? ""}</span>
        <span className="xox936i">as {partner}</span>
      </span>;
  const now = doseIn(dosed, edition);
  const word = trend(dosed, edition);
  const before = edition === 0 ? null : doseIn(dosed, edition === 1 ? 0 : 1);
  return <span className="x78zum5 x1q0g3np x16yl27p xskm3c9 x10pah57 x6s0dn4 x1uo3zyz">
      <span className="xox936i">{word}</span>
      <span className="xqu410z">{now === null ? `was ${before ?? ""}` : now}</span>
      {now !== null && before !== null && word !== "Same" ? <span className="xox936i">was {before}</span> : null}
    </span>;
}
function FilledCell({
  ingredient,
  index,
  drawn
}: {
  readonly ingredient: Ingredient;
  readonly index: number;
  readonly drawn: boolean;
}) {
  const dosed = DOSED.get(ingredient.name);
  const primary = PRIMARY[index] ?? true;
  const cell = CELLS[index];
  const first = CELLS.find(other => other.column === cell?.column);
  const partner = primary ? null : ROWS[first?.row ?? 0]?.name.toLowerCase() ?? null;
  return <>
      <span className="x10l6tqk xjlxmcz x1v7u6xw xb3r6kr x1hyvwdk xuxw1ft">{cellSaid(ingredient, primary)}</span>
      <span data-cell-slot="" aria-hidden="true" {...stylex.props(styles.slot, !drawn && styles.swatch, !drawn && SWATCH[ingredient.tones[0]])} />
      <span data-chip-dot="" aria-hidden="true" className={DOTS.get(ingredient.name)?.hidden} />
      <span aria-hidden="true" className="xt0psk2 xevc75t x2q0hrr xqu410z xuxw1ft">
        {ingredient.short}
      </span>
      {EDITION_LIST.map(edition => <span key={`w${edition}`} data-wide={edition} aria-hidden="true" className={edition === 0 ? WIDE_MARK.hidden : WIDE_MARK.gone}>
          <Mark dosed={dosed} edition={edition} partner={partner} />
        </span>)}
    </>;
}
function FindingRow({
  finding,
  row,
  mode,
  month,
  drawn
}: {
  readonly finding: Finding;
  readonly row: number;
  readonly mode: Mode;
  readonly month: Month;
  readonly drawn: boolean;
}) {
  const still = mode === "still";
  const results = resultsOf(finding);
  return <tr data-row={row} role="row" className={ROW_IDLE}>
      <th scope="row" role="rowheader" className="x1lliihq x18eilp1 x1l7klhg xm5moh1 x18r1ulw x15daork xxj6drn x1yc453h xox936i xt970qd x66opgc xgic4zv x1c1uobl x1hm6eoy xyri2b x1qxuy4o xt7dq6l xnveyj5 x1mbtngh x9f619 x1e5flnj">
        <span className="x1lliihq xqu410z x2q0hrr x1pa2vcq xfp7wzn">{finding.name}</span>
        <span className="x1lliihq x2q0hrr x1n6zpv0 x1jty25b xuxw1ft" aria-hidden="true">
          {results.map((result, i) => <span key={i} data-result={i} className={i === (still ? month : 0) ? RESULT_SHOWN : RESULT_GONE}>
              <ReadingOf result={result} />
            </span>)}
        </span>
        <span className="x10l6tqk xjlxmcz x1v7u6xw xb3r6kr x1hyvwdk xuxw1ft">{said(finding)}</span>
      </th>
      {INGREDIENTS.map((ingredient, column) => {
      const index = CELLS.findIndex(cell => cell.row === row && cell.column === column);
      return <td key={ingredient.name} role="cell" className={index === -1 ? CELL_NONE : still ? CELL_FILLED : CELL_WAITING} data-adds={index === -1 ? undefined : ingredient.name}>
            {index === -1 ? null : <FilledCell ingredient={ingredient} index={index} drawn={drawn} />}
          </td>;
    })}
    </tr>;
}
function measure(origin: DOMRect, el: Element | null): Box {
  const rect = el?.getBoundingClientRect();
  if (!rect) return {
    x: -999,
    y: -999,
    w: 0,
    h: 0
  };
  return {
    x: rect.left - origin.left,
    y: rect.top - origin.top,
    w: rect.width,
    h: rect.height
  };
}
interface Refs {
  readonly track: HTMLElement;
  readonly stage: HTMLElement;
  readonly inner: HTMLElement;
  readonly canvas: HTMLCanvasElement;
  readonly steps: HTMLElement;
  readonly head: HTMLElement;
  readonly milestones: HTMLElement;
  readonly fill: HTMLElement;
  readonly table: HTMLElement;
  readonly box: HTMLElement;
  readonly side: HTMLElement;
  readonly corner: HTMLElement;
  readonly fit: Fit;
}

/**
 * How the table sits in the stage's room. Where it does not fit at full size and the window is
 * under 900px, it is clipped to `available` pixels tall and slides up as the scan moves down its
 * rows, so its type is never shrunk. `ends` is where each row stops, measured from the table's top.
 */
interface Fit {
  windowed: boolean;
  available: number;
  height: number;
  ends: readonly number[];
}
interface Synced {
  step: number;
  row: number;
  formula: number;
}
function setClass(el: Element | null, name: string) {
  if (el && el.className !== name) el.className = name;
}
function setStyle(el: HTMLElement, property: "opacity" | "transform" | "display", value: string) {
  if (el.style[property] !== value) el.style[property] = value;
}

/** The row a scan is on, in whichever stage is reading results, or -1 between scans. */
function scanning(p: number): number {
  const phase = phaseAt(p);
  const scan = (at: (row: number) => number, clock: number) => ROWS.findIndex((_, row) => clock >= at(row) && clock < at(row + 1));
  if (phase === "reading") return scan(rowAt, firstOf(p));
  if (phase === "labs-3") return scan(rescanAt, laterOf(0, p));
  return phase === "labs-6" ? scan(rescanAt, laterOf(1, p)) : -1;
}

/** The newest formula the steps have reached, by step: the one the cells, note and legend show. */
const madeAt = (step: number): Edition => {
  if (step >= STEP_OF["formula-6"]) return 2;
  return step >= STEP_OF["formula-3"] ? 1 : 0;
};

/**
 * Where there is no 3D pill the cell's own swatch stands in for it, and goes once the mark replaces
 * it. It then gives up its room too, so a chip in the list is not left with a gap.
 */
function syncSlot(td: Element, waiting: boolean) {
  const slot = td.querySelector("[data-cell-slot]");
  if (!(slot instanceof HTMLElement)) return;
  setStyle(slot, "opacity", waiting ? "1" : "0");
  setStyle(slot, "display", waiting ? "" : "none");
  /* The list's chip takes the colour dot over from the pill, so it is never without one. */
  const dot = DOTS.get(td.getAttribute("data-adds") ?? "");
  if (dot) setClass(td.querySelector("[data-chip-dot]"), waiting ? dot.hidden : dot.shown);
}

/** One filled cell: tinted once read, then the dose or mark of whichever formula its row shows. */
function syncCell(td: Element, row: number, q: number, edition: Edition) {
  const index = CELL_INDEX.get(td) ?? 0;
  const left = q >= (LEAVES[index] ?? 2);
  const dosed = DOSED.get(td.getAttribute("data-adds") ?? "");
  const read = q >= rowAt(row);
  setClass(td, read ? CELL_OF_WORD[dosed ? trend(dosed, edition) : "Adds"] : CELL_WAITING);
  syncSlot(td, edition === 0 && !left);
  for (const each of EDITION_LIST) {
    const state = each !== edition ? "gone" : edition === 0 && !left ? "hidden" : "shown";
    setClass(td.querySelector(`[data-wide="${each}"]`), WIDE_MARK[state]);
  }
}
function syncRows(refs: Refs, p: number) {
  const q = firstOf(p);
  const made = madeAt(STEP_OF[phaseAt(p)]);
  refs.inner.querySelectorAll("tr[data-row]").forEach(tr => {
    const row = Number(tr.getAttribute("data-row") ?? 0);
    const edition = resultAt(row, p);
    tr.querySelectorAll("[data-result]").forEach(span => {
      setClass(span, Number(span.getAttribute("data-result")) === edition ? RESULT_SHOWN : RESULT_GONE);
    });

    /* A row's new result shows as the scan reads it, but its doses wait for the formula step, so
       the cells never show a formula whose pouch is not yet in front. */
    const doses: Edition = edition < made ? edition : made;
    tr.querySelectorAll("td[data-adds]").forEach(td => syncCell(td, row, q, doses));
  });
}

/** Below 1280px, moves between the table's scene and the pouches'. A still screen has both. */
function syncScene(refs: Refs, p: number, still: boolean) {
  const scenes = !still && window.matchMedia(SCENES_QUERY).matches;
  const b = scenes ? sceneAt(p) : 0;
  setStyle(refs.table, "opacity", scenes ? String(1 - b) : "");
  setStyle(refs.side, "transform", scenes ? `translateY(${(1 - b) * 115}%)` : "");
  /* Slid down, a clipped line of its text still showed under the upload bar. */
  setStyle(refs.side, "opacity", scenes && b < 0.02 ? "0" : "");
}

/** True when the address is empty or already names a month, so it is not another link's anchor. */
function ownsHash(): boolean {
  const {
    hash
  } = window.location;
  return hash === "" || monthOfHash(hash) !== null;
}

/** The step list, the step in the stage header, and the progress bar. */
function syncSteps(refs: Refs, p: number, last: Synced, still: boolean) {
  const step = STEP_OF[phaseAt(p)];
  const stage = STAGE_OF_STEP[step] ?? 0;
  if (step !== last.step) {
    last.step = step;
    refs.track.dataset.step = String(step + 1);
    const classes = stepClasses();
    refs.steps.querySelectorAll("li[data-stage]").forEach((li, i) => {
      const state = i === stage ? classes.now : i < stage ? classes.done : classes.next;
      setClass(li, state.step);
      setClass(li.querySelector("[data-number]"), state.number);
    });
    refs.head.querySelectorAll("li").forEach((li, i) => {
      setClass(li, i === stage ? HEAD_STEP_SHOWN : HEAD_STEP);
    });
  }
  const formula = formulaAt(p);

  /* The bar rests on a month's milestone through its stage, so a month button and a still screen,
     which both land where a stage ends, put it in the same place. */
  const share = still ? formula / 2 : barAt(p);
  refs.fill.style.transform = `scaleX(${share})`;
  if (formula !== last.formula) {
    /* Scrolling into another month keeps the address true, so a reload or a shared link lands
       there. Not on the first sync, which would put a hash on every visit. */
    if (last.formula !== -1 && !still && ownsHash()) {
      keepEntry(`#month-${MONTH_NUMBERS[formula]}`);
    }
    last.formula = formula;
    refs.track.dataset.formula = String(formula + 1);
    refs.milestones.querySelectorAll("button").forEach((button, i) => {
      const classes = MILESTONE_CLASSES[i];
      if (classes) setClass(button, i === formula ? classes.now : classes.idle);
      button.setAttribute("aria-pressed", String(i === formula));
    });
    refs.milestones.parentElement?.querySelectorAll("[data-rail-dot]").forEach((dot, i) => {
      const classes = RAIL_DOTS[i];
      if (classes) setClass(dot, i <= formula ? classes.reached : classes.idle);
    });
    refs.side.querySelector("[data-pouch]")?.setAttribute("aria-label", pouchLabel(formula));
    const results = refs.side.querySelector("[data-results]");
    if (results) results.textContent = resultsLine(formula);
    placeSide(refs);
  }
  const corner = cornerText(formula, madeAt(step));
  if (refs.corner.textContent !== corner) refs.corner.textContent = corner;
}

/** Applies `p` to everything that is not 3D: the steps, the scan, the cells, the note. */
function syncTable(refs: Refs, p: number, last: Synced, still: boolean) {
  refs.track.dataset.phase = phaseAt(p);
  syncSteps(refs, p, last, still);
  const row = still ? -1 : scanning(p);
  if (row !== last.row) {
    last.row = row;

    /* A row with no chip has nothing to read out, so the band skips it and never looks stuck. */
    refs.inner.querySelectorAll("tr[data-row]").forEach((tr, i) => {
      const reading = i === row && CHIP_ROWS.has(i);
      tr.className = reading ? ROW_READING : ROW_IDLE;
      tr.toggleAttribute("data-scanning", reading);
    });
  }
  syncRows(refs, p);
  syncScene(refs, p, still);
  slideTable(refs, p);
}

/** A pill for a row the window has slid out of sight waits at the window's edge, not over the page. */
function keepIn(box: Box, window_: Box | null): Box {
  if (!window_) return box;
  return {
    ...box,
    y: Math.min(Math.max(box.y, window_.y), window_.y + window_.h - box.h)
  };
}

/* Pills are sized from their table cell. The slot inside it only holds the flat fallback swatch. */
function layoutOf(refs: Refs, p: number, still: boolean): Layout {
  const origin = refs.canvas.getBoundingClientRect();
  const window_ = refs.fit.windowed ? measure(origin, refs.box) : null;
  return {
    dim: window.matchMedia(SCENES_QUERY).matches && !still ? sceneAt(p) : 0,
    head: [...refs.inner.querySelectorAll("[data-head-slot]")].map(el => measure(origin, el)),
    cells: [...refs.inner.querySelectorAll("td[data-adds]")].map(el => keepIn(measure(origin, el), window_)),
    pouch: measure(origin, refs.inner.querySelector("[data-pouch]"))
  };
}

/** The smallest a table that is not windowed is drawn at, as a share of its size. */
const FLOOR = 0.86;
const WINDOWED_QUERY = "(max-width: 899px)";

/** How far past the scanned row's bottom the window reaches, so the next row peeks in. */
const PEEK = 24;

/**
 * Makes the table fit the stage. Under 900px the stage cannot hold the list at a readable size, so
 * the table keeps its size, is clipped to the room there is and `slideTable` moves it. From 900px
 * it shrinks to fit, never below `FLOOR`: the window heights below that are a still screen. A
 * phone's browser bars take 150px or more of a screen, so the height a table must fit in is not
 * knowable from the width. The pills follow because they are placed from measured positions.
 */
function pinHead(table: HTMLElement, pinned: boolean) {
  const head = table instanceof HTMLTableElement ? table.tHead : null;
  if (!head) return;
  head.style.position = pinned ? "sticky" : "";
  head.style.top = pinned ? "0" : "";
  head.style.zIndex = pinned ? "1" : "";
  head.style.backgroundColor = pinned ? getComputedStyle(document.body).backgroundColor : "";
}
function fitTable(refs: Refs) {
  const {
    table,
    box,
    fit
  } = refs;
  table.style.transform = "";
  table.style.width = "";
  box.style.height = "";
  box.style.overflow = "";
  box.scrollTop = 0;
  pinHead(table, false);
  fit.windowed = false;
  fit.ends = [];
  const stage = refs.stage.getBoundingClientRect();
  const height = table.getBoundingClientRect().height;
  const padding = Number.parseFloat(getComputedStyle(refs.inner).paddingBottom);
  const available = stage.bottom - box.getBoundingClientRect().top - padding;
  if (height <= available) return;
  if (window.matchMedia(WINDOWED_QUERY).matches) {
    const top = table.getBoundingClientRect().top;
    fit.windowed = true;
    fit.available = available;
    fit.height = height;
    fit.ends = [...table.querySelectorAll("tr[data-row]")].map(row => row.getBoundingClientRect().bottom - top);
    box.style.height = `${available}px`;
    box.style.overflow = "hidden";
    pinHead(table, true);
    return;
  }
  const scale = Math.max(FLOOR, available / height);
  if (scale >= 0.995) return;
  table.style.transform = `scale(${scale})`;
  table.style.width = `${100 / scale}%`;
}

/** The row, as a fraction, the scan has reached: 2.5 is halfway from the third row to the fourth. */
function scanRow(p: number): number {
  const phase = phaseAt(p);
  const later = phase === "labs-3" ? 0 : phase === "labs-6" ? 1 : null;
  const rescan = later !== null;
  const clock = rescan ? laterOf(later, p) : firstOf(p);
  const at = rescan ? rescanAt : rowAt;
  if (!rescan && phase !== "reading") return ROWS.length - 1;
  const row = ROWS.findIndex((_, i) => clock < at(i + 1));
  if (row === -1) return ROWS.length - 1;
  const from = at(row);
  return Math.min(ROWS.length - 1, row + Math.max(0, (clock - from) / (at(row + 1) - from)));
}

/** Slides a windowed table so the row being scanned, and a peek of the next, is in view. */
function slideTable(refs: Refs, p: number) {
  const {
    fit,
    box
  } = refs;
  if (!fit.windowed) return;
  const reach = (row: number) => Math.min(Math.max(0, (fit.ends[row] ?? fit.height) + PEEK - fit.available), Math.max(0, fit.height - fit.available));
  const at = scanRow(p);
  const row = Math.floor(at);
  const offset = reach(row) + (reach(Math.min(ROWS.length - 1, row + 1)) - reach(row)) * (at - row);

  /* Scrolled, not moved, so the label at the table's head stays put above the rows. */
  box.scrollTop = Math.round(offset);
}

/** Space kept between the month's text and the pouches' scene, which starts where the table does. */
const SCENE_GAP = 24;

/**
 * Below 1280px the pouches' scene starts a gap below the month's own text, so a short month gives
 * the pouch the room the tallest month holds back for the table. The table starts lower because
 * its row waits for the tallest month, and a pouch placed there left 110px or more of blank.
 */
function placeSide(refs: Refs) {
  const scenes = window.matchMedia(SCENES_QUERY).matches;
  const shown = refs.head.children[Number(refs.track.dataset.formula ?? 1) - 1];

  /* From 900px the steps sit beside the stage and its own header is hidden, so the progress bar
     is the last thing above the table. */
  const above = refs.head.offsetParent === null ? refs.milestones : shown?.lastElementChild ?? refs.table;
  const bottom = above.getBoundingClientRect().bottom;
  const top = bottom - refs.inner.getBoundingClientRect().top;
  refs.side.style.top = scenes ? `${Math.round(top) + SCENE_GAP}px` : "";
}

/** Where a still screen rests, which the month buttons move, and how to draw it again. */
interface Pin {
  p: number;
  /** Set by a month button: the next frame draws where the page is, with no easing toward it. */
  snap: boolean;
  redraw: () => void;
}

/**
 * Drives the section from scroll. `scene` is null without WebGL, and the table and the steps still
 * follow the scroll, so the sequence reads the same with the photograph in place of the pouches. A
 * still section is drawn at the end of the chosen month's stage.
 */
function run(refs: Refs, scene: MapScene | null, still: boolean, pin: Pin): () => void {
  refs.inner.querySelectorAll("td[data-adds]").forEach((td, index) => CELL_INDEX.set(td, index));
  const last: Synced = {
    step: -1,
    row: -2,
    formula: -1
  };
  let shown = still ? pin.p : 0;
  const target = () => {
    const rect = refs.track.getBoundingClientRect();
    const length = rect.height - window.innerHeight;
    return length > 0 ? Math.min(1, Math.max(0, -rect.top / length)) : 0;
  };
  const frame = (now: number) => {
    if (still) shown = pin.p;else if (pin.snap) shown = target();else shown += (target() - shown) * 0.14;
    pin.snap = false;
    syncTable(refs, shown, last, still);
    scene?.draw(shown, still ? 0 : now / 1000, layoutOf(refs, shown, still));
  };
  const size = () => {
    if (!still) {
      fitTable(refs);
      placeSide(refs);
    }
    scene?.resize(refs.canvas.clientWidth, refs.canvas.clientHeight);
    frame(performance.now());
  };
  pin.redraw = () => frame(performance.now());
  const resize = new ResizeObserver(size);
  resize.observe(refs.stage);
  size();
  void document.fonts.ready.then(() => {
    scene?.redraw();
    size();
  });
  const stop = still ? () => {} : whileVisible(refs.stage, frame);
  return () => {
    stop();
    resize.disconnect();
    scene?.dispose();
  };
}

/** True when the section should be its still screen, and kept current as the window changes. */
function useStill(): boolean {
  const [still, setStill] = useState(() => window.matchMedia(STILL_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(STILL_QUERY);
    const change = () => setStill(query.matches);
    query.addEventListener("change", change);
    return () => query.removeEventListener("change", change);
  }, []);
  return still;
}
function StageFacts({
  stage,
  collapsed = false
}: {
  readonly stage: (typeof STAGES)[number];
  readonly collapsed?: boolean;
}) {
  return <dl {...{
    0: {
      className: "x1ghz6dp x1ri67zl x78zum5 xdt5ytf x626il7"
    },
    1: {
      className: "x1ghz6dp x1ri67zl x78zum5 xdt5ytf x626il7 x10l6tqk xpqkicq xjlxmcz x1n5zvcv x1v7u6xw xpv658r xb3r6kr x126i3ax x1hyvwdk xsmm3ln"
    }
  }[!!collapsed << 0]}>
      <div>
        <dt className="x10l6tqk xjlxmcz x1v7u6xw xb3r6kr x1hyvwdk xuxw1ft">Labs</dt>
        <dd className="x1ghz6dp x152e2qk x18t6ddr x1rf3zrh x1jty25b xbz0q3o x1fzhlzt">{stage.labs}</dd>
      </div>
      <div>
        <dt className="x10l6tqk xjlxmcz x1v7u6xw xb3r6kr x1hyvwdk xuxw1ft">Formula</dt>
        <dd className="x1ghz6dp x152e2qk x18t6ddr x1rf3zrh x1jty25b xbz0q3o x1fzhlzt">{stage.formula}</dd>
      </div>
    </dl>;
}
function StepList({
  listRef,
  still,
  month
}: {
  readonly listRef: RefObject<HTMLOListElement | null>;
  readonly still: boolean;
  readonly month: Month;
}) {
  return <ol ref={listRef} aria-label="What happens to Jordan's formula" {...{
    0: {
      className: "x1ghz6dp x1717udv xe8uvvx xexx8yu xd3y05d x18d9i69 x1bffcsl x10l6tqk xpqkicq xjlxmcz x1n5zvcv x1v7u6xw xpv658r xb3r6kr x126i3ax x1hyvwdk xsmm3ln"
    },
    1: {
      className: "x1ghz6dp x1717udv xe8uvvx xjh1bw0 x18d9i69 xb2g7nt xv4ixxy x78zum5 xdt5ytf x15agyyk"
    }
  }[!!still << 0]}>
      {STAGES.map((stage, i) => {
      const classes = stepClasses();
      const state = i === (still ? month : 0) ? classes.now : classes.next;
      return <li key={stage.month} data-stage={i} className={state.step}
      /* An inline height rather than a StyleX dynamic style. A dynamic style needs its
         inline variable applied with its class, and the scroll code swaps class names
         only, which left every block at its content height and the sequence far
         shorter than LENGTH. */ style={still ? undefined : {
        height: `${stageShare(i) * LENGTH}svh`
      }}>
            <div {...{
          0: {
            className: "x7wzq59 x185y1rg xrvj5dj xpczwk3 x1vdzsm x10ukxgv"
          },
          1: {
            className: "x185y1rg xrvj5dj xpczwk3 x1vdzsm x10ukxgv x1uhb9sk"
          }
        }[!!still << 0]}>
              <span data-number="" aria-hidden="true" className={state.number}>
                {stage.month.replace("Month ", "")}
              </span>
              <h3 className="x1ghz6dp x1u29x3t xqu410z x1d3j7mx x1e5flnj x1w2vvpw">{stage.month}</h3>
              <StageFacts stage={stage} collapsed={still && i !== month} />
            </div>
          </li>;
    })}
    </ol>;
}
function Progress({
  milestonesRef,
  fillRef,
  current,
  choose
}: {
  readonly milestonesRef: RefObject<HTMLDivElement | null>;
  readonly fillRef: RefObject<HTMLSpanElement | null>;
  readonly current: Month;
  readonly choose: (month: Month) => void;
}) {
  return <div className="xajvbnr x78zum5 x6s0dn4 x1r0x45p">
      <div className="x1n2onr6 x1iyjqo2">
        <span aria-hidden="true" className="x10l6tqk xxk6nc9 xu96u03 x3m8u43 xnp4br5 x1m3fsnl x1krkpam xb3r6kr">
          <span ref={fillRef} data-testid="progress-fill" className="x1lliihq x5yr21d xh8yej3 xmbr0q7 x4uogl2 x1ufz0iv x11xpdln x937rjr" />
        </span>
        {MILESTONES.map((label, i) => <span key={label} aria-hidden="true" data-rail-dot="" className={(i <= current ? RAIL_DOTS[i]?.reached : RAIL_DOTS[i]?.idle) ?? ""} />)}
        <div ref={milestonesRef} role="group" aria-label="Example month" className="xrvj5dj x185a7wo x1ghz6dp x1717udv xyinxu5 xx02khn xl4dl4e x1jty25b">
          {MILESTONES.map((label, i) => <button key={label} type="button" aria-pressed={i === current} onClick={() => choose(MONTHS[i] ?? 0)} className={(i === current ? MILESTONE_CLASSES[i]?.now : MILESTONE_CLASSES[i]?.idle) ?? ""}>
              {label}
            </button>)}
        </div>
      </div>
    </div>;
}
function LabTable({
  tableRef,
  boxRef,
  cornerRef,
  mode,
  month,
  drawn
}: {
  readonly tableRef: RefObject<HTMLTableElement | null>;
  readonly boxRef: RefObject<HTMLDivElement | null>;
  readonly cornerRef: RefObject<HTMLSpanElement | null>;
  readonly mode: Mode;
  readonly month: Month;
  readonly drawn: boolean;
}) {
  const still = mode === "still";
  const legendId = useId();
  const monthId = useId();
  return <div ref={boxRef} className="x1bagk7n x1y8v6su xeuugli">
      {/* One static name. Its description says which month's labs it shows, then how to read it, and
          both are drawn before any script runs. */}
      <table ref={tableRef} role="table" aria-label="Jordan's results and the doses each adds, an example" aria-describedby={`${monthId} ${legendId}`} className="x1lliihq x2qd0eg xh8yej3 x1mwwwfo xhdg8po x140o2bo x1l1vl4p x1hj3fc7 x19991ni x937rjr">
        <caption id={legendId} className="x1lliihq x1fiv64o x1jyo4x1 x2q0hrr x1n6zpv0 xzb3lse x1yc453h">
          Each result adds the doses beside it. A result in range at the first labs adds nothing.
          Each word says what the new results did to a dose, and &ldquo;was&rdquo; shows the earlier
          one. Omega{NB}3 ships beside the pouch, not in it.
        </caption>
        <colgroup>
          <col className="xrmyp2 xroyate xrnwunz xwqd1rt" />
          {INGREDIENTS.map(ingredient => <col key={ingredient.name} />)}
        </colgroup>
        <thead role="rowgroup" className="x1lliihq x1pedx6l">
          <tr role="row" className="x78zum5 x1yomz4y x1a02dak x6s0dn4 x1vdzsm xcfm6fe xk0dswh">
            <th scope="col" role="columnheader" {...{
            0: {
              className: "x1lliihq x18eilp1 x1l7klhg x1yc453h x3ajldb x2q0hrr xqu410z xzb3lse xk0dswh x103ahzr xm46snq"
            },
            1: {
              className: "x1lliihq x18eilp1 x1l7klhg x1yc453h x3ajldb x2q0hrr xqu410z xzb3lse xk0dswh x2lwn1j"
            }
          }[!!still << 0]}>
              <span ref={cornerRef} id={monthId} aria-hidden="true">
                {cornerText(still ? month : 0, still ? month : 0)}
              </span>
              <span className="x10l6tqk xjlxmcz x1v7u6xw xb3r6kr x1hyvwdk xuxw1ft">Jordan's results, an example</span>
            </th>
            {INGREDIENTS.map(ingredient => <th key={ingredient.name} scope="col" role="columnheader" abbr={ingredient.name} className="x1s85apg x18eilp1 x3ajldb xk0dswh xnjsko4 x1gw11ll x2q0hrr x1n6zpv0 xqu410z x1e5flnj">
                <span className="x78zum5 x1q0g3np x16yl27p x6s0dn4 xskm3c9 x9utrdf">
                  <span data-head-slot="" aria-hidden="true" {...stylex.props(styles.headSlot, !drawn && styles.swatch, !drawn && SWATCH[ingredient.tones[0]])} />
                  <span className="x2b8uid">{ingredient.short}</span>
                </span>
              </th>)}
          </tr>
        </thead>
        <tbody role="rowgroup" className="x1lliihq x1rrf47d">
          {LAB_ROWS.map((finding, i) => <FindingRow key={finding.name} finding={finding} row={i} mode={mode} month={month} drawn={drawn} />)}
        </tbody>
        <tbody role="rowgroup" className="x1lliihq x1rrf47d">
          {ANSWER_ROWS.map((finding, i) => <FindingRow key={finding.name} finding={finding} row={LAB_ROWS.length + i} mode={mode} month={month} drawn={drawn} />)}
        </tbody>
      </table>
    </div>;
}

/* Started when the page's code loads rather than after the first render, so the 3D is fetched
   while React is still building the page. */
const MAP_SCENE = import("./render/mapScene.ts").catch(() => null);
export function LabMap() {
  const track = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const steps = useRef<HTMLOListElement>(null);
  const head = useRef<HTMLOListElement>(null);
  const milestones = useRef<HTMLDivElement>(null);
  const fill = useRef<HTMLSpanElement>(null);
  const table = useRef<HTMLTableElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const fit = useRef<Fit>({
    windowed: false,
    available: 0,
    height: 0,
    ends: []
  });
  const side = useRef<HTMLDivElement>(null);
  const corner = useRef<HTMLSpanElement>(null);
  const flatCanvas = useRef<HTMLCanvasElement>(null);
  const still = useStill();
  const [month, setMonth] = useState<Month>(() => monthOfHash(window.location.hash) ?? 0);
  const pin = useRef<Pin>({
    p: MONTH_ENDS[month] ?? 1,
    snap: false,
    redraw: () => {}
  });
  const [scene, setScene] = useState<"pending" | "3d" | "none">("pending");
  const mode: Mode = still ? "still" : SCENE_MODE[scene];
  /* Flat marks and the photograph only without 3D. While the 3D loads the slots stay empty, since
     the flat stand-ins flashed on screen before the real pills replaced them. */
  const drawn = scene !== "none";
  useEffect(() => {
    const refs = track.current && stageRef.current && inner.current && canvas.current && steps.current && head.current && milestones.current && fill.current && table.current && box.current && side.current && corner.current ? {
      track: track.current,
      stage: stageRef.current,
      inner: inner.current,
      canvas: canvas.current,
      steps: steps.current,
      head: head.current,
      milestones: milestones.current,
      fill: fill.current,
      table: table.current,
      box: box.current,
      side: side.current,
      corner: corner.current,
      fit: fit.current
    } : null;
    if (!refs) return;
    const ready = refs;
    let stop: (() => void) | null = null;
    let gone = false;
    ready.side.style.top = "";
    ready.side.style.transform = "";
    ready.table.style.opacity = "";

    /* The table follows the scroll at once. The 3D joins when it has loaded. */
    stop = run(ready, null, still, pin.current);
    const late = window.setTimeout(() => {
      setScene(now => now === "pending" ? "none" : now);
    }, 8000);
    const flat = flatCanvas.current;
    void MAP_SCENE.then(module => {
      if (!module) return null;
      const live = module.mountMapScene(ready.stage, ready.canvas, COLUMNS, CELLS, EDITIONS);
      if (live) return {
        scene: live,
        live: true
      };
      return flat ? {
        scene: module.mountFlatMap(ready.stage, flat, EDITIONS),
        live: false
      } : null;
    }).catch(() => null).then(mounted => {
      if (gone) {
        mounted?.scene.dispose();
        return;
      }
      window.clearTimeout(late);
      setScene(mounted?.live ? "3d" : "none");
      if (!mounted) return;
      stop?.();
      stop = run(ready, mounted.scene, still, pin.current);
    });
    return () => {
      gone = true;
      window.clearTimeout(late);
      stop?.();
    };
  }, [still]);

  /* Shows a month: a still screen swaps in its table and pouch, a scrolling one scrolls to where
     the month's stage starts. */
  const show = (next: Month) => {
    announceJump();
    if (still) {
      pin.current.p = MONTH_ENDS[next] ?? 1;
      flushSync(() => setMonth(next));
      pin.current.redraw();
      /* The buttons sit under the steps, so without this the text above them is the last month's
         and the chosen month's is off screen. Nothing moves when its step is already in view. */
      track.current?.querySelector(`[data-stage="${String(next)}"]`)?.scrollIntoView({
        block: "nearest",
        behavior: "instant"
      });
      return;
    }
    const section = track.current;
    if (!section) return;
    const length = section.offsetHeight - window.innerHeight;
    const top = section.getBoundingClientRect().top + window.scrollY;

    /* Instant: the page scrolls smoothly, and the sequence eases toward the position while a
       visitor scrolls. A button press lands on the month's finished state with nothing played. */
    window.scrollTo({
      top: top + (MONTH_ENDS[next] ?? 1) * length,
      behavior: "instant"
    });
    pin.current.snap = true;
    pin.current.redraw();
  };
  const [said, setSaid] = useState("");
  const snapping = useJump();
  const choose = (next: Month) => {
    setSaid(before => `Showing month ${String(MONTH_NUMBERS[next])}. ${STAGES[next]?.labs ?? ""}${before.endsWith(ZERO_WIDTH) ? "" : ZERO_WIDTH}`);
    keepEntry(`#month-${String(MONTH_NUMBERS[next])}`);
    show(next);
  };

  /* A link to #month-3 opens the example at month 3, and so does editing the address. The first
     call waits for fonts, since the table's height, and so the scroll position, settles with them. */
  useEffect(() => {
    const open = () => {
      const named = monthOfHash(window.location.hash);
      if (named !== null) show(named);
    };
    void document.fonts.ready.then(open);
    window.addEventListener("hashchange", open);
    return () => window.removeEventListener("hashchange", open);
    // `show` closes over the current mode, so the listener is renewed when the mode changes.
  }, [still]);
  return <div>
      <div id="how" className="x18zekx4 xvueqy4 xb2g7nt x1j9m8v2 x78zum5 xdt5ytf x626il7">
        <h2 id="labs-heading" className="x1ghz6dp x154s7bo xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw">
          What your labs add
        </h2>
        <p className="x1ghz6dp xc0i79g x11mdwsw x1jty25b x1fzhlzt">
          Jordan's labs at month 1, 3 and 6, and the formula each one makes. The values are
          illustrative.
        </p>
        {/* The example is several screens of scrolling, so a visitor after the price or the
            choices can step past it. */}
        <a href="#questions" {...stylex.props(styles.skipExample, focusRing)}>
          Skip the example
        </a>
      </div>
      <section ref={track} id="labs" aria-labelledby="labs-heading" data-testid="lab-map" data-sequence="" data-mode={mode} data-phase="reading" data-step="1" data-formula="1" {...stylex.props(styles.track, snapping && instant)}>
        {/* Pressing a month changes a table and a pouch without moving focus, so say so. */}
        <p role="status" className="x10l6tqk xjlxmcz x1v7u6xw xb3r6kr x1hyvwdk xuxw1ft">
          {said}
        </p>
        <StepList listRef={steps} still={still} month={month} />
        <div {...{
        0: {
          className: "x1n2onr6 x15fg0br xpv658r x3pvysb"
        },
        1: {
          className: "x1n2onr6 xt7dq6l"
        }
      }[!!still << 0]}>
          <div ref={stageRef} {...{
          0: {
            className: "x7wzq59 xr317jx x13vifvy x187pvsn x3pvysb xb3r6kr"
          },
          1: {
            className: "x13vifvy xb3r6kr x1n2onr6 xt7dq6l"
          }
        }[!!still << 0]}>
            <div ref={inner} {...{
            0: {
              className: "x1n2onr6 x1ja2u2z x5yr21d xb2g7nt xv4ixxy xeonxcd x170965d x1eeskg9 xxh1dlw x1k1y2me xvydhls x9f619 xrvj5dj x1mkdm3x xd6parj x1swg1mr x1oo4ovd xgr3due x1qxlq8j x19c25m2 x1gh87bp x10ukxgv x18b05un x1ov5ho6 xqecrbx x1xje1ex"
            },
            1: {
              className: "x1n2onr6 x1ja2u2z xb2g7nt xv4ixxy xeonxcd x170965d x1eeskg9 x9f619 xrvj5dj x1mkdm3x xd6parj x1swg1mr x1oo4ovd xgr3due x1qxlq8j x19c25m2 x1gh87bp x10ukxgv x18b05un x1ov5ho6 xqecrbx x1xje1ex xt7dq6l xxh1dlw x1xhm6uz"
            }
          }[!!still << 0]}>
              <ol ref={head} aria-hidden="true" {...{
              0: {
                className: "x4lrnbh xrvj5dj x12iyxos x1ghz6dp x1717udv xe8uvvx"
              },
              1: {
                className: "x4lrnbh x1ghz6dp x1717udv xe8uvvx x1s85apg"
              }
            }[!!still << 0]}>
                {STAGES.map((stage, i) => <li key={stage.month} className={i === (still ? STAGES.length - 1 : 0) ? HEAD_STEP_SHOWN : HEAD_STEP}>
                    <p className="x1ghz6dp x1u29x3t xqu410z x1d3j7mx x1e5flnj">{stage.month}</p>
                    <StageFacts stage={stage} />
                  </li>)}
              </ol>
              <Progress milestonesRef={milestones} fillRef={fill} current={still ? month : 0} choose={choose} />
              <LabTable tableRef={table} boxRef={box} cornerRef={corner} mode={mode} month={month} drawn={drawn} />
              <div ref={side} {...{
              0: {
                className: "x1sgvmt9 xulbtqr x78zum5 xdt5ytf x6s0dn4 x626il7 xeuugli x10l6tqk xatg0b3 xu96u03 x1nr4raw x3m8u43 x1ho9cri xj9fbq8 xu4op86 xs37dp xb2g7nt xv4ixxy x9f619"
              },
              1: {
                className: "x78zum5 xdt5ytf x6s0dn4 x626il7 xeuugli xu96u03 x1nr4raw x3m8u43 x1ho9cri xj9fbq8 xu4op86 xs37dp x9f619 x1uhb9sk x52lvju xulbtqr xnjsko4"
              }
            }[!!still << 0]}>
                {/* The table says the same in full, so a screen reader skips this. */}
                <p data-results="" aria-hidden="true" {...{
                0: {
                  className: "x1ghz6dp xkh2ocl x2q0hrr xzb3lse x1fzhlzt x1lliihq xav5vni"
                },
                1: {
                  className: "x1ghz6dp xkh2ocl x2q0hrr xzb3lse x1fzhlzt x1s85apg"
                }
              }[!!still << 0]}>
                  {resultsLine(0)}
                </p>
                <div data-pouch="" role="img" aria-label={pouchLabel(still ? month : 0)} {...{
                0: {
                  className: "x1n2onr6 xh8yej3 x1iyjqo2 x2lwn1j"
                },
                1: {
                  className: "x1n2onr6 xh8yej3 x2lwn1j x1c4vz4f x1ki1rwx xlsnlc"
                }
              }[!!still << 0]}>
                  {/* The pouch's own label drawn flat, where the 3D cannot run. */}
                  <canvas ref={flatCanvas} aria-hidden="true" {...{
                  0: {
                    className: "x10l6tqk x10a8y8t xh8yej3 x5yr21d x19kjcj4"
                  },
                  1: {
                    className: "x10l6tqk x10a8y8t xh8yej3 x5yr21d x19kjcj4 x1s85apg"
                  }
                }[!!(scene !== "none") << 0]} />
                </div>
              </div>
            </div>
            <canvas ref={canvas} aria-hidden="true" className="x10l6tqk x10a8y8t xh8yej3 x5yr21d x47corl x1vjfegm" />
            <Tones />
          </div>
        </div>
      </section>
    </div>;
}